(() => {
  const CLAVE = 'mellinas_garaje';
  const reducirMovimiento = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esMovil = () => matchMedia('(max-width: 749px)').matches;

  // el coche viene de localStorage: nos fiamos solo de strings con forma esperada
  const cocheValido = (c) =>
    c && ['marca', 'modelo', 'gen', 'etiqueta', 'token'].every((k) => typeof c[k] === 'string' && c[k]);

  const leer = () => {
    try {
      const c = JSON.parse(localStorage.getItem(CLAVE));
      return cocheValido(c) ? c : null;
    } catch {
      return null;
    }
  };

  const guardar = (coche) => {
    try {
      coche ? localStorage.setItem(CLAVE, JSON.stringify(coche)) : localStorage.removeItem(CLAVE);
    } catch {}
    document.dispatchEvent(new CustomEvent('garaje:cambio', { detail: coche }));
  };

  let promesaCatalogo;
  const cargarCatalogo = (url) =>
    (promesaCatalogo ??= fetch(url)
      .then((r) => r.json())
      .catch(() => ({ marcas: [] })));

  // lee la curva de muelle de los tokens; si el navegador no soporta linear() cae a ease-out
  const muelle = (nombre) => {
    const valor = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();
    return valor && CSS.supports('transition-timing-function', valor) ? valor : 'ease-out';
  };

  // qué colección de aceite le toca a cada tipo de vehículo: c = coche, m4 = moto 4T o scooter, m2 = 2 tiempos
  const COLECCIONES = { c: 'aceite-motor', m4: 'aceite-de-moto', m2: 'aceite-mezcla-2t' };
  const raiz = () => window.Shopify?.routes?.root ?? '/';
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

  // ya no filtramos por metafield (habría que etiquetar producto a producto): la sección de recomendados cruza viscosidades
  const urlResultados = (coche) => `${raiz()}collections/${COLECCIONES[coche.tipo] ?? 'all'}#para-tu-vehiculo`;

  // ids como "suzuki" y "suzuki-moto" empiezan igual: gana siempre el más largo
  const porPrefijo = (lista, token) =>
    lista.filter((x) => token.startsWith(`${x.id}-`)).sort((a, b) => b.id.length - a.id.length)[0];

  const resolver = (catalogo, token) => {
    const marca = porPrefijo(catalogo.marcas, token);
    const modelo = marca && porPrefijo(marca.modelos, token.slice(marca.id.length + 1));
    const gen = modelo && modelo.gens.find((g) => token === `${marca.id}-${modelo.id}-${g.id}`);
    return gen ? { marca, modelo, gen } : null;
  };

  const tipoDe = (marca, perfil) => (perfil[0] === '2t' ? 'm2' : marca.tipo === 'moto' ? 'm4' : 'c');

  class GarajeSelector extends HTMLElement {
    async connectedCallback() {
      this.formulario = this.querySelector('form');
      this.campos = Object.fromEntries(['marca', 'modelo', 'anio'].map((n) => [n, this.querySelector(`[name=${n}]`)]));
      this.boton = this.querySelector('[type=submit]');
      this.catalogo = await cargarCatalogo(this.dataset.catalogo);

      this.rellenarMarcas();
      this.formulario.addEventListener('change', (e) => e.target.name === 'tipo' && this.alCambiarTipo());
      this.campos.marca.addEventListener('change', () => this.alCambiarMarca());
      this.campos.modelo.addEventListener('change', () => this.alCambiarModelo());
      this.campos.anio.addEventListener('change', () => (this.boton.disabled = !this.campos.anio.value));
      this.formulario.addEventListener('submit', (e) => this.enviar(e));

      const coche = leer();
      if (coche) this.restaurar(coche);
    }

    rellenar(campo, opciones, titulo) {
      const select = this.campos[campo];
      select.replaceChildren(new Option(titulo, ''), ...opciones.map(([v, t]) => new Option(t, v)));
      select.disabled = opciones.length === 0;
    }

    tipoElegido() {
      return this.formulario.elements.tipo.value;
    }

    rellenarMarcas() {
      const marcas = this.catalogo.marcas.filter((m) => m.tipo === this.tipoElegido());
      this.rellenar('marca', marcas.map((m) => [m.id, m.nombre]), 'Marca');
    }

    alCambiarTipo() {
      this.rellenarMarcas();
      this.rellenar('modelo', [], 'Modelo');
      this.rellenar('anio', [], 'Año');
      this.boton.disabled = true;
    }

    marcaActual() {
      return this.catalogo.marcas.find((m) => m.id === this.campos.marca.value);
    }

    modeloActual() {
      return this.marcaActual()?.modelos.find((m) => m.id === this.campos.modelo.value);
    }

    alCambiarMarca() {
      const modelos = this.marcaActual()?.modelos ?? [];
      this.rellenar('modelo', modelos.map((m) => [m.id, m.nombre]), 'Modelo');
      this.rellenar('anio', [], 'Año');
      this.boton.disabled = true;
    }

    alCambiarModelo() {
      const gens = this.modeloActual()?.gens ?? [];
      const actual = new Date().getFullYear();
      const anios = new Set();
      gens.forEach((g) => {
        for (let a = g.desde; a <= (g.hasta ?? actual); a++) anios.add(a);
      });
      const lista = [...anios].sort((a, b) => b - a).map((a) => [String(a), String(a)]);
      this.rellenar('anio', lista, 'Año');
      this.boton.disabled = true;
    }

    restaurar(coche) {
      const encontrado = resolver(this.catalogo, coche.token);
      if (!encontrado) return;
      const { marca, modelo } = encontrado;
      this.formulario.elements.tipo.value = marca.tipo;
      this.rellenarMarcas();
      this.campos.marca.value = marca.id;
      this.alCambiarMarca();
      this.campos.modelo.value = modelo.id;
      this.alCambiarModelo();
      if (coche.anio) this.campos.anio.value = coche.anio;
      this.boton.disabled = !this.campos.anio.value;
    }

    enviar(e) {
      e.preventDefault();
      const marca = this.marcaActual();
      const modelo = this.modeloActual();
      const anio = Number(this.campos.anio.value);
      const gen = modelo?.gens.find((g) => anio >= g.desde && anio <= (g.hasta ?? 9999));
      if (!gen) return;

      const visc = this.catalogo.perfiles?.[gen.p] ?? [];
      const coche = {
        marca: marca.id,
        modelo: modelo.id,
        gen: gen.id,
        anio: String(anio),
        etiqueta: `${marca.nombre} ${modelo.nombre} ${anio}`,
        token: `${marca.id}-${modelo.id}-${gen.id}`,
        tipo: tipoDe(marca, visc),
        visc,
      };
      guardar(coche);
      location.href = urlResultados(coche);
    }
  }

  class GarajeHoja extends HTMLElement {
    connectedCallback() {
      this.dialogo = this.querySelector('dialog');
      this.panel = this.querySelector('[data-panel]');
      this.asa = this.querySelector('[data-asa]');

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-garaje-abrir]')) this.abrir(e.target.closest('[data-garaje-abrir]'));
      });
      this.dialogo.addEventListener('click', (e) => e.target === this.dialogo && this.cerrar());
      this.dialogo.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.cerrar();
      });
      // el cierre puede llegar por swipe, Esc, click fuera o el botón: aquí se desbloquea siempre
      this.dialogo.addEventListener('close', () => document.documentElement.classList.remove('garaje-bloqueado'));
      this.querySelector('[data-cerrar]').addEventListener('click', () => this.cerrar());
      this.querySelector('[data-ver-piezas]').addEventListener('click', () => {
        const coche = leer();
        if (coche) location.href = urlResultados(coche);
      });
      this.querySelector('[data-quitar]').addEventListener('click', () => {
        guardar(null);
        location.href = this.dataset.coleccion;
      });
      document.addEventListener('garaje:cambio', () => this.pintarEstado());
      this.pintarEstado();
      this.hacerArrastrable();
    }

    pintarEstado() {
      const coche = leer();
      this.querySelector('[data-con-coche]').hidden = !coche;
      if (coche) this.querySelector('[data-etiqueta-coche]').textContent = coche.etiqueta;
    }

    // en móvil sube desde abajo; en escritorio nace del botón que la abrió (origen anclado)
    abrir(origen) {
      if (this.dialogo.open) return;
      this.origen = origen;
      this.dialogo.showModal();
      document.documentElement.classList.add('garaje-bloqueado');
      if (reducirMovimiento()) return;

      if (esMovil()) {
        this.panel.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
          duration: 600,
          easing: muelle('--muelle-suave'),
        });
      } else {
        const r = origen.getBoundingClientRect();
        const p = this.panel.getBoundingClientRect();
        this.panel.style.transformOrigin = `${r.left + r.width / 2 - p.left}px ${r.bottom - p.top}px`;
        this.panel.animate(
          [
            { transform: 'scale(0.92)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: 500, easing: muelle('--muelle-suave') }
        );
      }
    }

    cerrar(desde = 0, velocidad = 0) {
      if (!this.dialogo.open) return;
      if (reducirMovimiento()) return this.dialogo.close();
      const alto = this.panel.getBoundingClientRect().height;
      const destino = esMovil() ? `translateY(${alto}px)` : 'scale(0.94)';
      const origenAnim = esMovil() ? `translateY(${desde}px)` : 'scale(1)';
      // si el usuario soltó con inercia, la salida dura menos
      const duracion = Math.max(160, 320 - Math.min(Math.abs(velocidad) / 8, 160));
      const anim = this.panel.animate(
        [
          { transform: origenAnim, opacity: 1 },
          { transform: destino, opacity: esMovil() ? 1 : 0 },
        ],
        { duration: duracion, easing: 'ease-in', fill: 'forwards' }
      );
      anim.finished.then(() => {
        this.dialogo.close();
        anim.cancel();
      }, () => {});
    }

    // arrastrar la asa hacia abajo: 1:1 con el dedo, y al soltar decide con la velocidad proyectada
    hacerArrastrable() {
      let y = 0;
      let historial = [];
      let activo = false;

      const yActual = () => {
        const m = new DOMMatrixReadOnly(getComputedStyle(this.panel).transform);
        return m.m42;
      };

      this.asa.addEventListener('pointerdown', (e) => {
        if (!esMovil()) return;
        // interrumpible: si estaba animando, arrancamos desde donde está ahora mismo
        const enVuelo = this.panel.getAnimations().length > 0;
        y = enVuelo ? yActual() : 0;
        this.panel.getAnimations().forEach((a) => a.cancel());
        this.panel.style.transform = `translateY(${y}px)`;
        this.inicioY = e.clientY - y;
        historial = [{ t: e.timeStamp, y: e.clientY }];
        activo = true;
        this.asa.setPointerCapture(e.pointerId);
      });

      this.asa.addEventListener('pointermove', (e) => {
        if (!activo) return;
        const dy = e.clientY - this.inicioY;
        // hacia arriba resiste (rubber-band), hacia abajo sigue al dedo
        y = dy >= 0 ? dy : -Math.sqrt(-dy) * 2.2;
        this.panel.style.transform = `translateY(${y}px)`;
        historial.push({ t: e.timeStamp, y: e.clientY });
        if (historial.length > 6) historial.shift();
      });

      const soltar = () => {
        if (!activo) return;
        activo = false;
        const primero = historial[0];
        const ultimo = historial[historial.length - 1];
        const velocidad = ultimo.t > primero.t ? ((ultimo.y - primero.y) / (ultimo.t - primero.t)) * 1000 : 0;
        // proyección de inercia como la de apple: dónde acabaría si lo dejáramos deslizar
        const proyectado = y + (velocidad / 1000) * (0.998 / (1 - 0.998));
        const alto = this.panel.getBoundingClientRect().height;
        this.panel.style.transform = '';

        if (proyectado > alto * 0.5) {
          this.cerrar(y, velocidad);
        } else {
          this.panel.animate([{ transform: `translateY(${y}px)` }, { transform: 'translateY(0)' }], {
            duration: 600,
            easing: muelle('--muelle-firme'),
          });
        }
      };
      this.asa.addEventListener('pointerup', soltar);
      this.asa.addEventListener('pointercancel', soltar);
    }
  }

  customElements.define('garaje-selector', GarajeSelector);
  customElements.define('garaje-hoja', GarajeHoja);

  const moneda = (n) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: window.Shopify?.currency?.active ?? 'EUR' }).format(n);

  // products.json de la colección: trae tags, precios y stock sin tocar nada en el admin
  const cacheProductos = {};
  const productosDe = (handle) =>
    (cacheProductos[handle] ??= fetch(`${raiz()}collections/${handle}/products.json?limit=250`)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((j) => j.products ?? [])
      .catch(() => []));

  const el = (tag, clase, texto) => {
    const n = document.createElement(tag);
    if (clase) n.className = clase;
    if (texto != null) n.textContent = texto;
    return n;
  };

  const verVisc = (visc) =>
    visc[0] === '2t' ? 'Mezcla 2T' : visc.map((v) => v.toUpperCase().replace(/^(\d+)W(\d+)$/, '$1W-$2')).join(' · ');

  const tarjeta = (p, mejor) => {
    const li = el('li', 'reco-tarjeta');
    const a = el('a', 'reco-tarjeta__enlace');
    a.href = `${raiz()}products/${p.handle}`;
    const marco = el('span', 'reco-tarjeta__imagen');
    const src = p.images?.[0]?.src;
    if (src) {
      const img = document.createElement('img');
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=400`;
      img.alt = '';
      img.loading = 'lazy';
      marco.append(img);
    }
    if (mejor) marco.append(el('span', 'reco-tarjeta__sello', 'La mejor opción'));
    const precios = p.variants.map((v) => Number(v.price));
    const minimo = Math.min(...precios);
    const desde = precios.some((x) => x !== minimo);
    // la variante más barata es la que enseñamos: si ESA tiene precio de comparación, hay oferta
    const variante = p.variants.find((v) => Number(v.price) === minimo);
    const antes = Number(variante?.compare_at_price);
    const enOferta = antes > minimo;
    if (enOferta) marco.append(el('span', 'reco-tarjeta__sello reco-tarjeta__sello--oferta', 'Oferta'));

    const precio = el('span', `reco-tarjeta__precio${enOferta ? ' reco-tarjeta__precio--oferta' : ''}`);
    if (desde) precio.append('Desde ');
    if (enOferta) precio.append(el('s', 'reco-tarjeta__precio-antes', moneda(antes)), ' ');
    precio.append(moneda(minimo));

    a.append(marco, el('span', 'reco-tarjeta__nombre', p.title), precio);
    li.append(a);
    return li;
  };

  // "lo mejor para tu vehículo": cruza las viscosidades que pide el vehículo con las etiquetas de cada aceite
  class GarajeRecomendados extends HTMLElement {
    connectedCallback() {
      this.vacio = this.querySelector('[data-vacio]');
      this.contenido = this.querySelector('[data-contenido]');
      document.addEventListener('garaje:cambio', () => this.pintar());
      this.pintar();
    }

    // guardados de antes de tener viscosidades: los completamos desde el catálogo
    async completar(coche) {
      const catalogo = await cargarCatalogo(this.dataset.catalogo);
      const r = resolver(catalogo, coche.token);
      if (!r) return coche;
      const visc = catalogo.perfiles?.[r.gen.p] ?? [];
      const nuevo = { ...coche, tipo: tipoDe(r.marca, visc), visc };
      try {
        localStorage.setItem(CLAVE, JSON.stringify(nuevo));
      } catch {}
      pintarBadges();
      return nuevo;
    }

    enlaceWhatsapp(coche, texto) {
      const a = el('a', 'reco__whatsapp', texto);
      const mensaje = `Hola, ¿qué aceite necesito para mi ${coche.etiqueta}?`;
      a.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(mensaje)}`;
      a.target = '_blank';
      a.rel = 'noopener';
      return a;
    }

    async pintar() {
      const turno = (this.turno = (this.turno ?? 0) + 1);
      let coche = leer();
      this.vacio.hidden = !!coche;
      this.contenido.hidden = !coche;
      if (!coche) return;

      if (!coche.visc) coche = await this.completar(coche);
      if (turno !== this.turno) return;

      const esMoto = coche.tipo !== 'c';
      const complementos = (esMoto ? this.dataset.complementosMoto : this.dataset.complementosCoche)?.split(/\s+/).filter(Boolean) ?? [];
      const [aceites, ...otros] = await Promise.all([
        productosDe(COLECCIONES[coche.tipo] ?? 'aceite-motor'),
        ...complementos.map(productosDe),
      ]);
      if (turno !== this.turno) return;

      const visc = (coche.visc ?? []).map(norm);
      const mejores = aceites
        .map((p) => {
          const tags = p.tags.map(norm);
          const puesto = coche.tipo === 'm2' ? 0 : tags.includes('camion') ? -1 : visc.findIndex((v) => tags.includes(v));
          return { p, puesto, hay: p.variants.some((v) => v.available), diesel: tags.includes('diesel') };
        })
        .filter((x) => x.puesto >= 0)
        // no sabemos si el coche es gasolina o diésel: a igual viscosidad, el aceite específico de diésel va detrás
        .sort((a, b) => Number(b.hay) - Number(a.hay) || a.puesto - b.puesto || Number(a.diesel) - Number(b.diesel))
        .slice(0, Number(this.dataset.max) || 8);

      const precioMin = (p) => Math.min(...p.variants.map((v) => Number(v.price)));
      // de cada colección, los 2 con stock más baratos: así no salen packs de 48 unidades
      const otrosProductos = otros.flatMap((lista) =>
        lista.filter((p) => p.variants.some((v) => v.available)).sort((a, b) => precioMin(a) - precioMin(b)).slice(0, 2)
      );

      const cabecera = el('div', 'reco__cabecera');
      cabecera.append(el('p', 'reco__eyebrow', 'Tu vehículo'), el('h2', 'reco__titulo', `Lo mejor para tu ${coche.etiqueta}`));
      if (coche.visc?.length) {
        const texto = el('p', 'reco__texto');
        texto.append('Viscosidad habitual: ', el('strong', null, verVisc(coche.visc)), '. Confírmala siempre con el manual de tu vehículo.');
        cabecera.append(texto);
      }
      const acciones = el('div', 'reco__acciones');
      const cambiar = el('button', 'button button--secondary', 'Cambiar vehículo');
      cambiar.type = 'button';
      cambiar.setAttribute('data-garaje-abrir', '');
      acciones.append(cambiar, this.enlaceWhatsapp(coche, '¿Dudas? Te lo confirmamos'));
      cabecera.append(acciones);

      const bloques = [cabecera];
      if (mejores.length) {
        const lista = el('ul', 'reco__rejilla');
        mejores.forEach((x, i) => lista.append(tarjeta(x.p, i === 0 && x.hay)));
        bloques.push(lista);
      } else {
        const aviso = el('p', 'reco__aviso', 'Ahora mismo no tenemos un aceite con esa viscosidad publicado. ');
        aviso.append(this.enlaceWhatsapp(coche, 'Escríbenos y te lo buscamos'));
        bloques.push(aviso);
      }
      if (otrosProductos.length) {
        const lista = el('ul', 'reco__rejilla reco__rejilla--extra');
        otrosProductos.forEach((p) => lista.append(tarjeta(p, false)));
        bloques.push(el('h3', 'reco__subtitulo', 'También te puede hacer falta'), lista);
      }
      this.contenido.replaceChildren(...bloques);
    }
  }
  customElements.define('garaje-recomendados', GarajeRecomendados);

  // etiqueta del coche en la píldora de la cabecera
  const pintarPildoras = () => {
    const coche = leer();
    document.querySelectorAll('[data-garaje-etiqueta]').forEach((el) => {
      el.textContent = coche ? coche.etiqueta : el.dataset.vacio;
    });
    document.querySelectorAll('[data-garaje-abrir]').forEach((el) => el.toggleAttribute('data-con-coche', !!coche));
  };

  // badge de compatibilidad: tres estados, y sin dato del producto nunca decimos "no compatible"
  const pintarBadge = (badge, coche) => {
    if (!coche) {
      badge.hidden = true;
      return;
    }
    const marca = `${coche.token}|${badge.dataset.tokens}`;
    if (badge.dataset.listo === marca) return;
    badge.dataset.listo = marca;

    const tokens = badge.dataset.tokens.split(/\s+/).filter(Boolean);
    badge.hidden = false;
    badge.replaceChildren();

    // sin tokens exactos, recomendamos por viscosidad y por tipo de producto (nunca un aceite de coche para una moto ni al revés)
    const tags = (badge.dataset.tags ?? '').split(/\s+/).map(norm);
    const tipoProducto = badge.dataset.tipo ?? '';
    // \b porque "Aceite de Motor" contiene "moto" y se colaba como aceite de moto
    const encajaTipo =
      coche.tipo === 'm2' ? /\b2t\b/i.test(tipoProducto) : coche.tipo === 'm4' ? /\bmoto\b/i.test(tipoProducto) : !/\b(moto|2t)\b/i.test(tipoProducto);
    const recomendado =
      !!coche.visc?.length && encajaTipo && !tags.includes('camion') && (coche.tipo === 'm2' || coche.visc.some((v) => tags.includes(norm(v))));

    if (tokens.length === 0 && recomendado) {
      badge.dataset.estado = 'encaja';
      badge.textContent = `Recomendado para tu ${coche.etiqueta}`;
    } else if (tokens.length === 0) {
      const texto = `Hola, ¿este producto (${badge.dataset.producto}) encaja en mi ${coche.etiqueta}?`;
      const enlace = document.createElement('a');
      enlace.href = `https://wa.me/${badge.dataset.whatsapp}?text=${encodeURIComponent(texto)}`;
      enlace.target = '_blank';
      enlace.rel = 'noopener';
      enlace.textContent = 'Comprobar por WhatsApp';
      badge.dataset.estado = 'revisar';
      badge.append(enlace);
    } else if (tokens.includes('universal')) {
      badge.dataset.estado = 'encaja';
      badge.textContent = 'Universal';
    } else if (tokens.includes(coche.token)) {
      badge.dataset.estado = 'encaja';
      badge.textContent = `Encaja con tu ${coche.etiqueta}`;
    } else {
      badge.dataset.estado = 'no';
      badge.textContent = `No compatible con tu ${coche.etiqueta}`;
    }
  };

  const pintarBadges = (raiz = document) => {
    const coche = leer();
    raiz.querySelectorAll('[data-compat-badge]').forEach((b) => pintarBadge(b, coche));
  };

  const pintarTodo = () => {
    pintarPildoras();
    pintarBadges();
  };

  document.addEventListener('garaje:cambio', pintarTodo);
  window.addEventListener('storage', (e) => e.key === CLAVE && pintarTodo());
  pintarTodo();

  // los facets de shopify repintan la rejilla por ajax: hay que pintar los badges nuevos
  let pendiente = false;
  new MutationObserver((mutaciones) => {
    const hayNuevos = mutaciones.some((m) =>
      [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-compat-badge]') || n.querySelector?.('[data-compat-badge]')))
    );
    if (!hayNuevos || pendiente) return;
    pendiente = true;
    requestAnimationFrame(() => {
      pendiente = false;
      pintarBadges();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // cualquier enlace de whatsapp (flotante, footer, menú...) sale con el vehículo del garaje, sin tocar cada plantilla
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[href*="wa.me/"]');
    const coche = a && leer();
    if (!coche) return;
    const url = new URL(a.href);
    const texto = url.searchParams.get('text') ?? '';
    if (texto.includes(coche.etiqueta)) return;
    url.searchParams.set('text', `${texto} (Mi vehículo: ${coche.etiqueta})`.trim());
    a.href = url.href;
  });

  window.Garaje = { leer, guardar, urlResultados };
})();
